<template>
  <div class="documents-page ahrs-home-bg">
    <div class="page-header">
      <div class="header-background"></div>
      <div class="header-content">
        <h1 class="page-title" data-aos="fade-down">Upload Documents</h1>
        <p class="page-subtitle" data-aos="fade-up" data-aos-delay="200">
          Attach your passport image and payment receipt
        </p>
      </div>
    </div>

    <div class="documents-container">
      <!-- Progress Steps -->
      <div class="progress-steps" data-aos="fade-up">
        <div class="step completed">
          <div class="step-number">1</div>
          <span class="step-label">Package Selection</span>
        </div>
        <div class="step completed">
          <div class="step-number">2</div>
          <span class="step-label">Billing Details</span>
        </div>
        <div class="step active">
          <div class="step-number">3</div>
          <span class="step-label">Upload Documents</span>
        </div>
      </div>

      <div class="documents-content">
        <!-- Upload Form -->
        <div class="upload-form-container" data-aos="fade-right">
          <div class="upload-form">
            <h2 class="form-title">Documents</h2>
            <p class="form-intro">
              Upload a clear scan or photo of the bio-data page of your
              passport and proof of your payment (bank slip, mobile money
              confirmation, or screenshot). Both files are optional &mdash; you
              can finish your registration without them and send them to us
              later by email.
            </p>

            <form @submit.prevent="finishRegistration">
              <!-- Passport Upload -->
              <div class="upload-block">
                <label for="passportInput" class="form-label">
                  Passport image <span class="optional-tag">(optional)</span>
                </label>
                <p class="form-hint">
                  Accepted formats: JPG, PNG, PDF · Max 5 MB
                </p>

                <div
                  class="dropzone"
                  :class="{
                    'has-file': passportFile,
                    'has-error': passportError,
                  }"
                  @click="triggerPassportInput"
                  @dragover.prevent
                  @drop.prevent="onPassportDrop"
                >
                  <input
                    ref="passportInputEl"
                    id="passportInput"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    class="hidden-input"
                    @change="onPassportChange"
                  />

                  <template v-if="!passportFile">
                    <svg
                      class="dropzone-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17 8 12 3 7 8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    <p class="dropzone-title">
                      Drop your passport here, or
                      <span class="dropzone-link">browse</span>
                    </p>
                  </template>

                  <template v-else>
                    <div class="file-preview">
                      <img
                        v-if="passportPreview"
                        :src="passportPreview"
                        alt="Passport preview"
                        class="preview-image"
                      />
                      <div v-else class="preview-doc">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>PDF</span>
                      </div>
                      <div class="file-info">
                        <span class="file-name">{{ passportFile.name }}</span>
                        <span class="file-size">{{ formatBytes(passportFile.size) }}</span>
                      </div>
                      <button
                        type="button"
                        class="file-remove"
                        @click.stop="clearPassport"
                        aria-label="Remove passport"
                      >
                        ×
                      </button>
                    </div>
                  </template>
                </div>
                <p v-if="passportError" class="field-error">{{ passportError }}</p>
              </div>

              <!-- Receipt Upload -->
              <div class="upload-block">
                <label for="receiptInput" class="form-label">
                  Payment receipt <span class="optional-tag">(optional)</span>
                </label>
                <p class="form-hint">
                  Accepted formats: JPG, PNG, PDF · Max 5 MB
                </p>

                <div
                  class="dropzone"
                  :class="{
                    'has-file': receiptFile,
                    'has-error': receiptError,
                  }"
                  @click="triggerReceiptInput"
                  @dragover.prevent
                  @drop.prevent="onReceiptDrop"
                >
                  <input
                    ref="receiptInputEl"
                    id="receiptInput"
                    type="file"
                    accept="image/jpeg,image/png,application/pdf"
                    class="hidden-input"
                    @change="onReceiptChange"
                  />

                  <template v-if="!receiptFile">
                    <svg
                      class="dropzone-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="1.6"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    >
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="9" y1="13" x2="15" y2="13" />
                      <line x1="9" y1="17" x2="13" y2="17" />
                    </svg>
                    <p class="dropzone-title">
                      Drop your payment receipt here, or
                      <span class="dropzone-link">browse</span>
                    </p>
                  </template>

                  <template v-else>
                    <div class="file-preview">
                      <img
                        v-if="receiptPreview"
                        :src="receiptPreview"
                        alt="Receipt preview"
                        class="preview-image"
                      />
                      <div v-else class="preview-doc">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>PDF</span>
                      </div>
                      <div class="file-info">
                        <span class="file-name">{{ receiptFile.name }}</span>
                        <span class="file-size">{{ formatBytes(receiptFile.size) }}</span>
                      </div>
                      <button
                        type="button"
                        class="file-remove"
                        @click.stop="clearReceipt"
                        aria-label="Remove receipt"
                      >
                        ×
                      </button>
                    </div>
                  </template>
                </div>
                <p v-if="receiptError" class="field-error">{{ receiptError }}</p>
              </div>

              <!-- Optional reference note -->
              <div class="form-group">
                <label for="paymentReference" class="form-label">
                  Payment reference / transaction ID (optional)
                </label>
                <input
                  id="paymentReference"
                  v-model="paymentReference"
                  type="text"
                  class="form-input"
                  placeholder="e.g. CRDB-TXN-9F8A12 or MPESA-QHF7B"
                />
              </div>

              <div class="form-actions">
                <button
                  type="button"
                  @click="goBack"
                  class="back-btn"
                  :disabled="isSubmitting"
                >
                  Back
                </button>
                <button
                  type="submit"
                  :disabled="isSubmitting"
                  class="next-btn"
                  :class="{ disabled: isSubmitting }"
                >
                  <span v-if="isSubmitting" class="loading-spinner"></span>
                  {{ isSubmitting ? 'Submitting...' : 'Finish' }}
                </button>
              </div>
            </form>
          </div>
        </div>

        <!-- Order Summary Sidebar -->
        <div class="order-summary-container" data-aos="fade-left">
          <div class="order-summary">
            <h3 class="summary-title">Order Summary</h3>

            <div class="order-items">
              <div class="order-item">
                <div class="item-details">
                  <h4>
                    {{ checkoutData?.pricing?.title || "Package Registration" }}
                  </h4>
                  <p class="item-subtitle">
                    {{
                      checkoutData?.pricing?.description ||
                      "Conference Registration"
                    }}
                  </p>
                </div>
              </div>

              <div class="selected-packages">
                <div class="package-item">
                  <span class="package-name">✓ Participation</span>
                  <span class="package-price">{{
                    formatPrice(
                      checkoutData?.pricing?.participation?.price || 0
                    )
                  }}</span>
                </div>
                <div v-if="checkoutData?.packages?.tour" class="package-item">
                  <span class="package-name">✓ Tour Package</span>
                  <span class="package-price">{{
                    formatPrice(checkoutData?.pricing?.tourPackage?.price || 0)
                  }}</span>
                </div>
                <div
                  v-if="checkoutData?.packages?.masterclass"
                  class="package-item"
                >
                  <span class="package-name">✓ Masterclass</span>
                  <span class="package-price">{{
                    formatPrice(checkoutData?.pricing?.masterclass?.price || 0)
                  }}</span>
                </div>
              </div>
            </div>

            <div class="order-totals">
              <div class="total-row subtotal">
                <span>Subtotal</span>
                <span>{{ formatPrice(checkoutData?.total || 0) }}</span>
              </div>
              <div class="total-row final-total">
                <span>Total</span>
                <span>{{ formatPrice(checkoutData?.total || 0) }}</span>
              </div>
            </div>

            <div class="event-info">
              <h4>Why we need these</h4>
              <p class="info-paragraph">
                Your passport image is used to issue your delegate badge and
                support visa invitation letters where applicable. The payment
                receipt is matched against the bank transfer details on the
                previous page and helps us confirm your registration faster.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Error Modal -->
  <Teleport to="body">
    <div v-if="showErrorModal" class="modal-overlay" @click.self="closeModal">
      <div class="modal-box">
        <div class="modal-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </div>
        <h3 class="modal-title">Submission Failed</h3>
        <p class="modal-message">{{ submitError }}</p>
        <button class="modal-close-btn" @click="closeModal">OK</button>
      </div>
    </div>
  </Teleport>

  <!-- Success Modal -->
  <Teleport to="body">
    <div v-if="showSuccessModal" class="modal-overlay" @click.self="finishAndGoHome">
      <div class="modal-box success-box">
        <div class="modal-icon success-icon">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
            <path d="M8 12.5l3 3 5-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <h3 class="modal-title success-title">Registration Complete</h3>
        <p class="modal-message">
          Thank you for registering for AHRS 2026. We have received your details
          and will follow up by email with confirmation and any next steps.
        </p>
        <button class="modal-close-btn success-btn" @click="finishAndGoHome">
          Back to Home
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { useRouter } from "vue-router";
import { registrationAPI } from "@/services/api";

const router = useRouter();

const checkoutData = ref(null);
const passportInputEl = ref(null);
const receiptInputEl = ref(null);

const passportFile = ref(null);
const receiptFile = ref(null);
const passportPreview = ref(null);
const receiptPreview = ref(null);
const passportError = ref("");
const receiptError = ref("");

const paymentReference = ref("");

const isSubmitting = ref(false);
const submitError = ref(null);
const showErrorModal = ref(false);
const showSuccessModal = ref(false);

const MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_TYPES = ["image/jpeg", "image/png", "application/pdf"];

onMounted(() => {
  const storedData = sessionStorage.getItem("checkoutData");
  if (storedData) {
    checkoutData.value = JSON.parse(storedData);

    if (!checkoutData.value.billingDetails) {
      router.push("/checkout");
      return;
    }

    if (checkoutData.value.paymentReference) {
      paymentReference.value = checkoutData.value.paymentReference;
    }
  } else {
    router.push("/registration");
  }
});

onBeforeUnmount(() => {
  if (passportPreview.value) URL.revokeObjectURL(passportPreview.value);
  if (receiptPreview.value) URL.revokeObjectURL(receiptPreview.value);
});

const closeModal = () => {
  showErrorModal.value = false;
};

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatPrice = (price) => {
  if (!price || !checkoutData.value?.pricing) return "";
  if (checkoutData.value.pricing.currency === "TZS") {
    return `TZS ${price.toLocaleString()}`;
  }
  return `USD ${price.toLocaleString()}`;
};

const validateFile = (file) => {
  if (!ACCEPTED_TYPES.includes(file.type)) {
    return "Only JPG, PNG, or PDF files are allowed.";
  }
  if (file.size > MAX_SIZE) {
    return "File is larger than 5 MB.";
  }
  return "";
};

const buildPreview = (file) => {
  if (file.type === "application/pdf") return null;
  return URL.createObjectURL(file);
};

const triggerPassportInput = () => passportInputEl.value?.click();
const triggerReceiptInput = () => receiptInputEl.value?.click();

const setPassport = (file) => {
  const err = validateFile(file);
  if (err) {
    passportError.value = err;
    return;
  }
  if (passportPreview.value) URL.revokeObjectURL(passportPreview.value);
  passportFile.value = file;
  passportPreview.value = buildPreview(file);
  passportError.value = "";
};

const setReceipt = (file) => {
  const err = validateFile(file);
  if (err) {
    receiptError.value = err;
    return;
  }
  if (receiptPreview.value) URL.revokeObjectURL(receiptPreview.value);
  receiptFile.value = file;
  receiptPreview.value = buildPreview(file);
  receiptError.value = "";
};

const onPassportChange = (e) => {
  const file = e.target.files?.[0];
  if (file) setPassport(file);
};

const onReceiptChange = (e) => {
  const file = e.target.files?.[0];
  if (file) setReceipt(file);
};

const onPassportDrop = (e) => {
  const file = e.dataTransfer?.files?.[0];
  if (file) setPassport(file);
};

const onReceiptDrop = (e) => {
  const file = e.dataTransfer?.files?.[0];
  if (file) setReceipt(file);
};

const clearPassport = () => {
  if (passportPreview.value) URL.revokeObjectURL(passportPreview.value);
  passportFile.value = null;
  passportPreview.value = null;
  passportError.value = "";
  if (passportInputEl.value) passportInputEl.value.value = "";
};

const clearReceipt = () => {
  if (receiptPreview.value) URL.revokeObjectURL(receiptPreview.value);
  receiptFile.value = null;
  receiptPreview.value = null;
  receiptError.value = "";
  if (receiptInputEl.value) receiptInputEl.value.value = "";
};

const goBack = () => {
  router.push("/checkout");
};

const fileMeta = (file, serverPath) => ({
  name: file.name,
  size: file.size,
  type: file.type,
  uploadedAt: new Date().toISOString(),
  serverPath: serverPath || null,
});

const finishRegistration = async () => {
  if (isSubmitting.value) return;

  isSubmitting.value = true;
  submitError.value = null;

  try {
    let uploadResponse = null;

    // Only hit the upload endpoint if the delegate actually attached at
    // least one file. The endpoint, the form fields, and the storage column
    // shape are documented in registration-data-format.md.
    if (passportFile.value || receiptFile.value) {
      const formData = new FormData();
      if (passportFile.value) formData.append("passport", passportFile.value);
      if (receiptFile.value) formData.append("receipt", receiptFile.value);
      formData.append("email", checkoutData.value.billingDetails.email);
      if (checkoutData.value.registrationId) {
        formData.append("registrationId", checkoutData.value.registrationId);
      }
      if (paymentReference.value) {
        formData.append("paymentReference", paymentReference.value);
      }
      uploadResponse = await registrationAPI.uploadDocuments(formData);
    }

    const documents = {
      passport: passportFile.value
        ? fileMeta(passportFile.value, uploadResponse?.documents?.passport)
        : null,
      receipt: receiptFile.value
        ? fileMeta(receiptFile.value, uploadResponse?.documents?.receipt)
        : null,
    };

    const updated = {
      ...checkoutData.value,
      paymentReference: paymentReference.value || null,
      documents,
    };
    sessionStorage.setItem("checkoutData", JSON.stringify(updated));

    // Finalise the registration. Payment method is left blank because
    // delegates settle offline via the bank details shown in Step 2.
    const completionData = {
      email: updated.billingDetails?.email,
      billingDetails: updated.billingDetails,
      total: updated.total,
      currency: updated.pricing?.currency || "USD",
      paymentData: {
        paymentMethod: null,
        orderId: "AHRS2026-" + Date.now(),
        orderDate: new Date().toISOString(),
        packages: updated.packages,
        pricing: updated.pricing,
        paymentReference: updated.paymentReference,
        documents: updated.documents,
      },
    };

    try {
      await registrationAPI.completePayment(completionData);
    } catch (completeErr) {
      // Backend completion failed but the upload (if any) already
      // succeeded. Surface a soft warning rather than blocking the user.
      console.warn("Registration completion error:", completeErr);
    }

    showSuccessModal.value = true;
  } catch (apiError) {
    console.error("Document upload error:", apiError);
    submitError.value =
      apiError.error ||
      apiError.message ||
      "We could not submit your registration. Please try again.";
    showErrorModal.value = true;
  } finally {
    isSubmitting.value = false;
  }
};

const finishAndGoHome = () => {
  showSuccessModal.value = false;
  sessionStorage.removeItem("checkoutData");
  router.push("/");
};
</script>

<style scoped>
.documents-page {
  min-height: 100vh;
  color: #fbf7ec;
  padding-top: 80px;
}

.page-header {
  position: relative;
  text-align: center;
  padding: 40px 20px 30px;
  overflow: hidden;
}

.header-background {
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse at center,
    rgba(74, 222, 128, 0.1) 0%,
    transparent 70%
  );
  animation: pulse 4s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 0.1; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.05); }
}

.header-content {
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
}

.page-title {
  font-size: 3rem;
  font-weight: 900;
  margin-bottom: 15px;
  color: #fbf7ec;
}

.page-subtitle {
  font-size: 1.2rem;
  color: rgba(251, 247, 236, 0.92);
  letter-spacing: 1px;
}

.documents-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px 20px;
}

.progress-steps {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 40px;
  gap: 16px;
  flex-wrap: wrap;
}

.step {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px 20px;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(74, 222, 128, 0.2);
  transition: all 0.3s ease;
}

.step.completed {
  background: rgba(74, 222, 128, 0.2);
  border-color: #b8f0c8;
}

.step.active {
  background: rgba(74, 222, 128, 0.15);
  border-color: #b8f0c8;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.3);
}

.step-number {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: var(--color-primary-light);
  color: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.9rem;
}

.step-label {
  font-weight: 600;
  color: rgba(251, 247, 236, 0.92);
  white-space: nowrap;
}

.documents-content {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  align-items: start;
}

.upload-form-container {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 20px;
  padding: 40px;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 10px;
  color: #b8f0c8;
}

.form-intro {
  color: rgba(251, 247, 236, 0.82);
  line-height: 1.6;
  margin-bottom: 28px;
  font-size: 0.98rem;
}

.upload-block {
  margin-bottom: 28px;
}

.form-label {
  display: block;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 6px;
  color: rgba(251, 247, 236, 0.92);
}

.form-hint {
  font-size: 0.85rem;
  color: rgba(251, 247, 236, 0.65);
  margin-bottom: 12px;
}

.required {
  color: #ef4444;
}

.optional-tag {
  color: rgba(251, 247, 236, 0.55);
  font-weight: 500;
  font-size: 0.85rem;
  margin-left: 4px;
}

.dropzone {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 170px;
  padding: 24px;
  background: rgba(74, 222, 128, 0.06);
  border: 2px dashed rgba(74, 222, 128, 0.4);
  border-radius: 14px;
  cursor: pointer;
  transition: all 0.25s ease;
}

.dropzone:hover {
  background: rgba(74, 222, 128, 0.1);
  border-color: #b8f0c8;
}

.dropzone.has-file {
  background: rgba(74, 222, 128, 0.1);
  border-style: solid;
  border-color: #b8f0c8;
  cursor: default;
}

.dropzone.has-error {
  border-color: rgba(239, 68, 68, 0.6);
  background: rgba(239, 68, 68, 0.06);
}

.hidden-input {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.dropzone-icon {
  width: 44px;
  height: 44px;
  color: #b8f0c8;
}

.dropzone-title {
  margin: 0;
  color: rgba(251, 247, 236, 0.92);
  font-size: 0.98rem;
  text-align: center;
}

.dropzone-link {
  color: #b8f0c8;
  text-decoration: underline;
}

.file-preview {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
}

.preview-image {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid rgba(74, 222, 128, 0.3);
}

.preview-doc {
  width: 70px;
  height: 70px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: rgba(74, 222, 128, 0.15);
  border: 1px solid rgba(74, 222, 128, 0.3);
  border-radius: 10px;
  color: #b8f0c8;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.preview-doc svg {
  width: 26px;
  height: 26px;
}

.file-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.file-name {
  color: #fbf7ec;
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file-size {
  color: rgba(251, 247, 236, 0.65);
  font-size: 0.82rem;
  margin-top: 2px;
}

.file-remove {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid rgba(239, 68, 68, 0.4);
  background: rgba(239, 68, 68, 0.1);
  color: #fca5a5;
  font-size: 1.4rem;
  line-height: 1;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.file-remove:hover {
  background: rgba(239, 68, 68, 0.25);
  color: #fff;
}

.field-error {
  margin-top: 8px;
  color: #fca5a5;
  font-size: 0.85rem;
}

.form-group {
  margin-bottom: 28px;
}

.form-input {
  width: 100%;
  padding: 15px 20px;
  background: rgba(74, 222, 128, 0.1);
  border: 2px solid rgba(74, 222, 128, 0.3);
  border-radius: 12px;
  color: #fbf7ec;
  font-size: 1rem;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.form-input::placeholder {
  color: rgba(251, 247, 236, 0.5);
}

.form-input:focus {
  outline: none;
  border-color: #b8f0c8;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.3);
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 30px;
  gap: 20px;
}

.back-btn,
.next-btn {
  padding: 15px 30px;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
}

.back-btn {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(251, 247, 236, 0.82);
  border: 2px solid rgba(255, 255, 255, 0.2);
}

.back-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.15);
  color: #fbf7ec;
}

.next-btn {
  background: linear-gradient(
    135deg,
    var(--color-primary-light),
    var(--color-success)
  );
  color: #fbf7ec;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 0 20px rgba(74, 222, 128, 0.4);
}

.next-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 0 30px rgba(74, 222, 128, 0.6);
}

.next-btn:disabled,
.next-btn.disabled {
  background: rgba(255, 255, 255, 0.1);
  color: rgba(251, 247, 236, 0.65);
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.loading-spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-right: 8px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Order summary sidebar */
.order-summary-container {
  position: sticky;
  top: 100px;
}

.order-summary {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(74, 222, 128, 0.2);
  border-radius: 20px;
  padding: 30px;
}

.summary-title {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 25px;
  color: #b8f0c8;
}

.order-item {
  margin-bottom: 20px;
}

.item-details h4 {
  color: #b8f0c8;
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 5px;
}

.item-subtitle {
  color: rgba(251, 247, 236, 0.65);
  font-size: 0.9rem;
}

.selected-packages {
  background: rgba(74, 222, 128, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin: 20px 0;
}

.package-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid rgba(74, 222, 128, 0.1);
}

.package-item:last-child {
  border-bottom: none;
}

.package-name {
  color: rgba(251, 247, 236, 0.92);
  font-weight: 500;
}

.package-price {
  color: #b8f0c8;
  font-weight: 700;
}

.order-totals {
  border-top: 2px solid rgba(74, 222, 128, 0.2);
  padding-top: 20px;
  margin-top: 20px;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
}

.subtotal {
  color: rgba(251, 247, 236, 0.82);
}

.final-total {
  color: #fbf7ec;
  font-size: 1.3rem;
  font-weight: 700;
  border-top: 1px solid rgba(74, 222, 128, 0.2);
  padding-top: 15px;
  margin-top: 10px;
}

.final-total span:last-child {
  color: #b8f0c8;
}

.event-info {
  background: rgba(74, 222, 128, 0.08);
  border-radius: 12px;
  padding: 20px;
  margin-top: 25px;
}

.event-info h4 {
  color: #b8f0c8;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 10px;
}

.info-paragraph {
  color: rgba(251, 247, 236, 0.82);
  font-size: 0.9rem;
  line-height: 1.55;
  margin: 0;
}

/* Responsive */
@media (max-width: 1200px) {
  .documents-content {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  .order-summary-container {
    position: relative;
    top: 0;
  }
}

@media (max-width: 768px) {
  .page-title { font-size: 2.2rem; }
  .progress-steps {
    flex-direction: column;
    gap: 12px;
  }
  .upload-form-container,
  .order-summary { padding: 25px; }
  .form-actions { flex-direction: column; }
  .back-btn, .next-btn { width: 100%; }
}

@media (max-width: 480px) {
  .page-title { font-size: 1.8rem; }
  .upload-form-container,
  .order-summary { padding: 20px; }
  .step-label { display: none; }
}

/* Error Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.modal-box {
  background: linear-gradient(135deg, #0f2419, #1a472a);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 20px;
  padding: 40px 36px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 30px rgba(239, 68, 68, 0.15);
}

.modal-icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 20px;
  color: #ef4444;
}

.modal-icon svg {
  width: 100%;
  height: 100%;
}

.modal-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #fca5a5;
  margin-bottom: 14px;
}

.modal-message {
  color: rgba(251, 247, 236, 0.82);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 28px;
}

.modal-close-btn {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fbf7ec;
  border: none;
  padding: 12px 40px;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal-close-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(239, 68, 68, 0.4);
}

/* Success Modal variants */
.success-box {
  border-color: rgba(184, 240, 200, 0.45);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6), 0 0 30px rgba(74, 222, 128, 0.18);
}

.success-icon {
  color: #b8f0c8;
}

.success-title {
  color: #b8f0c8;
}

.success-btn {
  background: linear-gradient(135deg, var(--color-primary-light), var(--color-success));
}

.success-btn:hover {
  box-shadow: 0 8px 20px rgba(74, 222, 128, 0.4);
}
</style>
